import type {
  AllocationCheck,
  LPLSubFirm,
} from '@/trading/types/api/edit-checks'
import type { TraderProfile } from '@/types/api/legacy-trader-profile'
import type { OrderType } from '@/types/enums/order'
import type { OrderSubmissionDialogPageState } from './order-submission-dialog'

export enum AllocationValidationStatus {
  InProgress = 'in-progress',
  Complete = 'complete',
  Error = 'error',
}

export enum StatVisualizerType {
  BlockOrders = 'block-orders',
  Allocations = 'allocations',
}

export enum ClientSideAllocationCheckStatus {
  Approved = 'approved',
  Review = 'review',
  Rejected = 'rejected',
}

export enum LPLPlatform {
  MANAGERSELECT = 'manager-select',
  MWP = 'mwp',
  PWP = 'pwp',
}

export type BulkUploadFlowType = 'edit_checks' | 'skip_checks' | 'street_trades'

export const LPLPlatformLabels: Record<LPLPlatform, string> = {
  [LPLPlatform.MANAGERSELECT]: 'Manager Select',
  [LPLPlatform.MWP]: 'MWP',
  [LPLPlatform.PWP]: 'PWP',
}

interface AllocationMetadata {
  price: number | null
  annotation: string
  settlementDate: number | null // YYYYMMDD format
}

export type AllocationMetadataMap = Record<string, AllocationMetadata>

export interface AllocationCheckRow extends AllocationCheck {
  instrument_id: string
  serverStatus: ClientSideAllocationCheckStatus
  manualStatus: ClientSideAllocationCheckStatus | null
  price: number | null
  annotation: string
  orderType: OrderType.Limit | OrderType.Market
  hasWarning: boolean
  settlementDate: number | null // YYYYMMDD format
}

export enum BulkUploadError {
  ServerError = 'server-error',
  OneSchemaUploadError = 'one-schema-upload-error',
  EditCheckCreationError = 'edit-check-creation-error',
  EditCheckPollingError = 'edit-check-polling-error',
  OrderSubmissionError = 'order-submission-error',
  MarketClosedError = 'market-closed-error',
}

export interface BulkUploadState {
  isImporterOpen: boolean
  allocationChecks: AllocationCheckRow[]
  error: BulkUploadError | null
  oneSchemaUserJwt: string | null
  restartFlow: boolean | null
  isOrderSubmissionDialogOpen: boolean
  traderProfile: TraderProfile | null
  allocationMetadata: AllocationMetadataMap
  skipsChecks: boolean
  filename: string | null
  initialOrderSubmissionDialogPageState: OrderSubmissionDialogPageState | null
  selectedTemplateKey: string | null
  flowType: BulkUploadFlowType | null
  /**
   * Internal path to return to after the upload is confirmed, when the flow was
   * deep-linked from the v2 blotter (null for the standard v1 flow). Set at
   * `initializeState`; the success screen navigates here instead of the trade
   * blotter. For the edit-checks flow it is also mirrored to sessionStorage
   * (keyed by editCheckId), since store values don't survive that page hop.
   */
  returnTo: string | null
}

export interface BulkUploadLPLSpreadsheetRecord {
  side: string
  accountId: string
  // TODO: Combine this with SpreadsheetRecord type to unify bulk order upload
  allocationAmount: string // quantity MUST ALWAYS come from One Schema as 'allocationAmount'
  instrumentId: string
  annotation: string
  price: string
  subfirm?: LPLSubFirm
  settlementDate?: string // MM/DD/YYYY or empty string from spreadsheet
}

/**
 * Domain type representing a parsed allocation record.
 * This is the result of parsing raw spreadsheet records and is used throughout
 * the bulk allocation upload flow (both skipChecks and editChecks paths).
 */
export interface ParsedAllocationRecord {
  clientAllocationId: string
  side: string // NOTE: Converted to OrderSide in uploadResultToRows and createEditChecksAllocations
  accountId: string
  allocationAmount: string // NOTE: Converted to number in uploadResultToRows and createEditChecksAllocations
  instrumentId: string
  annotation: string
  price: string // NOTE: Converted to number | null in uploadResultToRows and createAllocationMetadata
  subfirm?: LPLSubFirm
  settlementDate: number | null // YYYYMMDD format, parsed from MM/DD/YYYY spreadsheet input
}
